import { Flag } from '../components/Flag.js';
import { GlobeIcon } from '../ui/icons.js';
import { StatIcon } from '../ui/StatIcon.js';
import {
  Severity,
  type AddressServerVerdict,
  type AddressVerdict,
  type DecisionBy,
} from '@bartizo/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { getAddressVerdict } from '../api/addresses.js';
import { isApiError } from '../api/client.js';
import { useCurrentUser } from '../auth/session-context.js';
import { ActionDialog } from '../components/ActionDialog.js';
import { AddressStatusBadge, ServerStateBadge } from '../components/AddressStatus.js';
import { PageHeader } from '../components/PageHeader.js';
import { ReputationCard } from '../components/ReputationCard.js';
import { BlocklistAddPanel } from '../components/BlocklistAddPanel.js';
import { BlocklistStatusBadge, SeverityBadge } from '../components/StatusBadge.js';
import { addressStory, NOT_KEPT } from '../lib/address-story.js';
import { getBlocklistSummary, listBlocklist } from '../api/blocklist.js';
import { describeAction } from '../lib/actions.js';
import { effectiveTimezone, formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';

/**
 * One address, and whether it is stopped (P11.1).
 *
 * The question a person arrives with — "is this scanner blocked?" — is answered
 * first, in one line, then server by server: banned or not, by whom, until when.
 * What it did comes after, and what can be done about it is beside the answer.
 * Every number here is the API's; nothing is worked out in the browser.
 */

const BY_LABEL: Record<DecisionBy, string> = {
  crowdsec: 'CrowdSec',
  bartizo: 'Bartizo',
  manual: 'someone on the server',
  other: 'another source',
};

/** One sentence: the answer, before the detail. */
function headline(verdict: AddressVerdict): string {
  const reachable = verdict.servers.filter((server) => server.state !== 'unknown');
  const banned = verdict.servers.filter((server) => server.state === 'banned').length;
  switch (verdict.status) {
    case 'stopped':
      return `Banned on ${reachable.length === 1 ? 'the one server' : `all ${String(reachable.length)} servers`} that can enforce it.`;
    case 'partly':
      return `Banned on ${String(banned)} of ${String(reachable.length)} servers. The others still let it in.`;
    case 'pending':
      return 'A ban has been sent and no server has put it in place yet.';
    case 'open':
      return reachable.length === 0
        ? 'No server has said what CrowdSec holds yet, so nothing is known to stop it.'
        : 'Banned nowhere: every server still lets it in.';
    case 'protected':
      return verdict.protectedBy ?? 'A ban of this address would be refused.';
  }
}

/** What to do next, by status: the decision this page exists for. */
function advice(verdict: AddressVerdict): string | undefined {
  const busy = verdict.events > 0 || verdict.requests > 0;
  switch (verdict.status) {
    case 'open':
      return busy
        ? 'It has been active and nothing stops it. Ban it on every server, or ask the AI for a second opinion first.'
        : undefined;
    case 'partly':
      return 'Ban it everywhere so the servers that still let it in stop it too.';
    case 'stopped':
      return 'Nothing to do unless it comes back when the ban ends.';
    default:
      return undefined;
  }
}

export function AddressPage(): ReactNode {
  const { ip = '' } = useParams();
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const canAct = session.user.role !== 'viewer';
  const [dialog, setDialog] = useState<'ban_ip' | 'unban_ip' | 'blocklist' | null>(null);
  // P11.7: whether the fleet blocklist holds it, and the ladder a new entry starts on.
  const listed = useQuery({
    queryKey: ['blocklist', { q: ip, status: 'exact' }],
    queryFn: async () => (await listBlocklist({ q: ip })).items.find((entry) => entry.ip === ip),
    enabled: ip !== '',
  });
  const policy = useQuery({ queryKey: ['blocklist-summary'], queryFn: getBlocklistSummary });

  const verdict = useQuery({
    queryKey: ['address', ip],
    queryFn: () => getAddressVerdict(ip),
    // A ban on its way lands within seconds; ask again until it has.
    refetchInterval: (query) => (query.state.data?.status === 'pending' ? 5_000 : false),
  });

  if (verdict.isPending) {
    return (
      <div className="page">
        <div className="skeleton skeleton-title" aria-hidden="true" />
        <div
          className="skeleton skeleton-block"
          aria-busy="true"
          aria-label="Loading the address"
        />
      </div>
    );
  }
  if (verdict.isError) {
    return (
      <div className="page">
        <PageHeader title={ip} />
        <Alert tone="danger" title="Could not load this address">
          {isApiError(verdict.error) && verdict.error.status === 400
            ? 'That is not an IP address.'
            : 'Try again in a moment.'}{' '}
          <Link to="/traffic">Back to traffic</Link>.
        </Alert>
      </div>
    );
  }

  const data = verdict.data;
  const heldSomewhere = data.servers.some((server) =>
    server.bans.some((ban) => ban.by === 'bartizo' || ban.by === 'manual'),
  );
  const next = advice(data);
  const latestEvent = data.recentEvents[0];
  const story = addressStory(data);
  const reachable = data.servers.filter((server) => server.state !== 'unknown').length;
  const bannedOn = data.servers.filter((server) => server.state === 'banned').length;
  const seenOn = data.servers.filter((server) => server.events > 0 || server.requests > 0).length;
  const serverName = new Map(data.servers.map((server) => [server.serverId, server.serverName]));

  return (
    <div className="page">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/traffic">Traffic</Link>
        <span aria-hidden="true">›</span>
        <Link to="/traffic/ip">Addresses</Link>
        <span aria-hidden="true">›</span>
        <span aria-current="page">{data.ip}</span>
      </nav>
      <PageHeader
        title={data.ip}
        description="Is it stopped, where, by whom and until when — and what it has been doing."
        actions={
          <span className="row">
            <Link
              className="button button-secondary"
              to={`/traffic/ip?q=${encodeURIComponent(data.ip)}`}
            >
              Its requests
            </Link>
            {canAct && (
              <span className="row">
                {data.status !== 'protected' && data.status !== 'stopped' && (
                  <Button
                    variant="danger"
                    onClick={() => {
                      setDialog('ban_ip');
                    }}
                  >
                    Ban everywhere
                  </Button>
                )}
                {heldSomewhere && (
                  <Button
                    onClick={() => {
                      setDialog('unban_ip');
                    }}
                  >
                    Unban
                  </Button>
                )}
              </span>
            )}
          </span>
        }
      />

      <dl className="stat-tiles">
        <div className={`stat-tile ${data.events > 0 ? 'stat-tile-alert' : ''}`}>
          <dt>Events, 7 days</dt>
          <dd>{data.events.toLocaleString('en-US')}</dd>
          <p>
            {Object.keys(data.categories).length === 0
              ? 'None raised'
              : `${String(Object.keys(data.categories).length)} kinds`}
          </p>
        </div>
        <div className="stat-tile">
          <dt>Requests, 24 h</dt>
          <dd>{data.requests.toLocaleString('en-US')}</dd>
          <p>To websites Bartizo watches</p>
        </div>
        <div className="stat-tile">
          <dt>Servers</dt>
          <dd>
            {bannedOn}
            <span className="stat-of"> of {reachable} banned</span>
          </dd>
          <p>Seen on {seenOn}</p>
        </div>
        <div className="stat-tile">
          <StatIcon icon={GlobeIcon} />
          <dt>Where</dt>
          <dd className="stat-text">
            <Flag code={data.geo?.country ?? null} name />
          </dd>
          <p>
            {data.geo?.network ?? 'Network unknown'}
            {data.geo?.asn != null && ` · AS${String(data.geo.asn)}`}
          </p>
        </div>
        <div className="stat-tile">
          <dt>Last seen</dt>
          <dd className="stat-text">
            {data.lastSeenAt === null ? '—' : formatRelative(data.lastSeenAt)}
          </dd>
          <p>
            {data.firstSeenAt === null
              ? 'Never seen'
              : `First ${formatInstant(data.firstSeenAt, { timezone })}`}
          </p>
        </div>
      </dl>

      <Card
        title="Verdict"
        aside={<AddressStatusBadge status={data.status} />}
        description={`Checked ${formatRelative(data.checkedAt)}.`}
      >
        <p className="verdict-line">{headline(data)}</p>
        {next !== undefined && <p className="muted">{next}</p>}
        {data.suggestion !== null && (
          <p className="text-sm">
            The AI looked at it {formatRelative(data.suggestion.createdAt)}: “
            {data.suggestion.title}” — it recommends{' '}
            <strong>{data.suggestion.recommendation}</strong> ({data.suggestion.status}).{' '}
            <Link to="/suggestions">See suggestions</Link>.
          </p>
        )}
        {data.suggestion === null && latestEvent !== undefined && canAct && (
          <p className="text-sm muted">
            Want a second opinion? Open its{' '}
            <Link to={`/events/${latestEvent.id}`}>latest event</Link> and ask the AI for a
            recommendation.
          </p>
        )}
      </Card>

      <div className="split-grid">
        <Card title="What it has been doing" description="Told from the counts Bartizo keeps.">
          {story.map((line) => (
            <p key={line} className="text-sm">
              {line}
            </p>
          ))}
          <p className="muted text-sm">{NOT_KEPT}</p>
        </Card>
        <ReputationCard ip={data.ip} />
      </div>

      <Card
        title="Fleet blocklist"
        description="Kept out of every server, new ones too, longer than one CrowdSec ban."
        {...(listed.data !== undefined
          ? { aside: <BlocklistStatusBadge status={listed.data.status} /> }
          : {})}
      >
        {listed.data?.status === 'active' ? (
          <p className="text-sm">
            On the list until {formatRelative(listed.data.until)} — step {listed.data.level} of the
            ladder. {listed.data.reason}. <Link to="/blocklist">Open the blocklist</Link>.
          </p>
        ) : (
          <div className="row">
            <p className="text-sm muted">
              {listed.data === undefined
                ? 'Not on the list.'
                : listed.data.status === 'removed'
                  ? `Taken off by ${listed.data.removedByEmail ?? 'a person'}: ${listed.data.removedReason ?? ''}`
                  : listed.data.status === 'protected'
                    ? listed.data.refused
                    : 'Was on the list; its ban has ended.'}
            </p>
            {canAct && data.status !== 'protected' && listed.data?.status !== 'protected' && (
              <Button
                className="push-right"
                onClick={() => {
                  setDialog('blocklist');
                }}
              >
                Add to the blocklist
              </Button>
            )}
          </div>
        )}
      </Card>

      <Card title="On each server">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Server</th>
                <th scope="col">State</th>
                <th scope="col">Why, and until</th>
                <th scope="col">Events (7 days)</th>
                <th scope="col">Requests (24 h)</th>
                <th scope="col">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {data.servers.map((server) => (
                <ServerRow key={server.serverId} server={server} timezone={timezone} />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card
        title="Recent events"
        aside={
          <Link to="/events" className="text-sm">
            All events →
          </Link>
        }
      >
        {Object.keys(data.categories).length > 0 && (
          <p className="muted text-sm">
            {Object.entries(data.categories)
              .sort(([, a], [, b]) => b - a)
              .map(([category, n]) => `${category} ${String(n)}`)
              .join(' · ')}
          </p>
        )}
        {data.recentEvents.length === 0 ? (
          <p className="muted text-sm">No events from this address in the last week.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Severity</th>
                  <th scope="col">What happened</th>
                  <th scope="col" className="wide-only">
                    Server
                  </th>
                  <th scope="col">When</th>
                </tr>
              </thead>
              <tbody>
                {data.recentEvents.map((event) => (
                  <tr key={event.id}>
                    <td>
                      <EventSeverity severity={event.severity} />
                    </td>
                    <td>
                      <Link to={`/events/${event.id}`}>{event.message}</Link>
                      <span className="cell-sub">{event.category}</span>
                    </td>
                    <td className="wide-only">
                      <Link to={`/servers/${encodeURIComponent(event.serverId)}`}>
                        {serverName.get(event.serverId) ?? event.serverId}
                      </Link>
                    </td>
                    <td title={formatInstant(event.occurredAt, { timezone })}>
                      {formatRelative(event.occurredAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {data.actions.length > 0 && (
        <Card title="Bans and unbans asked for">
          <ul className="text-sm">
            {data.actions.map((action) => (
              <li key={action.id}>
                <Link to={`/actions/${action.id}`}>{describeAction(action)}</Link>{' '}
                <span className="muted">
                  {action.requestedByEmail ?? action.source} · {formatRelative(action.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {dialog === 'blocklist' && (
        <BlocklistAddPanel
          ladder={policy.data?.policy.ladder_seconds ?? []}
          initialIp={data.ip}
          onDone={() => {
            setDialog(null);
            void listed.refetch();
          }}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {(dialog === 'ban_ip' || dialog === 'unban_ip') && (
        <ActionDialog
          type={dialog}
          initialIp={data.ip}
          onClose={() => {
            setDialog(null);
            void verdict.refetch();
          }}
        />
      )}
    </div>
  );
}

/** The API sends a severity as text; a word it does not know is shown as it is. */
function EventSeverity({ severity }: { severity: string }): ReactNode {
  const parsed = Severity.safeParse(severity);
  return parsed.success ? <SeverityBadge severity={parsed.data} /> : <span>{severity}</span>;
}

function ServerRow({
  server,
  timezone,
}: {
  server: AddressServerVerdict;
  timezone: string;
}): ReactNode {
  const [ban] = server.bans;
  return (
    <tr>
      <td>
        <Link to={`/servers/${encodeURIComponent(server.serverId)}`}>{server.serverName}</Link>
        {server.serverStatus !== 'active' && (
          <span className="cell-sub">{server.serverStatus}</span>
        )}
      </td>
      <td>
        <ServerStateBadge state={server.state} />
      </td>
      <td>
        {ban !== undefined ? (
          <>
            {ban.type === 'ban' ? 'Banned' : ban.type} by {BY_LABEL[ban.by]}
            {ban.scenario !== null && <span className="cell-sub">{ban.scenario}</span>}
            <span className="cell-sub">
              until {formatInstant(ban.until, { timezone })}
              {ban.value.includes('/') ? ` (range ${ban.value})` : ''}
            </span>
          </>
        ) : server.skipped !== null ? (
          <span className="muted">{server.skipped}</span>
        ) : server.decisionsReportedAt === null ? (
          <span className="muted">Its agent has not reported what CrowdSec holds yet.</span>
        ) : (
          <span className="muted">
            CrowdSec holds nothing for it (reported {formatRelative(server.decisionsReportedAt)}).
          </span>
        )}
      </td>
      <td>{server.events.toLocaleString('en-US')}</td>
      <td>{server.requests.toLocaleString('en-US')}</td>
      <td>{server.lastSeenAt === null ? '—' : formatRelative(server.lastSeenAt)}</td>
    </tr>
  );
}
